import { getContractReviewType } from "~/components/contract/contract-review.model";
import {
  getStatusBadgeRole,
  isBadgedAsValidatorOfOwnContract,
} from "~/components/contract/contract-status-badge.model";
import { useUserMe } from "~/components/user/composables/useUser";
import { useAsapWorkspace } from "~/components/workspace/composables/useWorkspace";
import { usePosthog } from "~/composables/usePosthog";
import { useToast } from "~/composables/useToast";
import type { Color } from "@asap/design/styles/colors";
import type { ContractKeyTransmissionType } from "@asap/shared/schemas/contract/base";
import type { PostContractWithAutoMissionBody } from "@asap/shared/schemas/contract/contract-with-auto-mission";
import type { PostModificationAmendmentBody } from "@asap/shared/schemas/contract/modification-amendment";
import type { PostExtensionAmendmentBody } from "@asap/shared/schemas/contract/extension-amendment";
import type { RemunerationProfileInsertPayload } from "@asap/shared/schemas/remuneration/remuneration-profile";
import type { ContractListItem, ContractsQueryData } from "@asap/shared/schemas/contract/list";
import type { TeamMemberSelectionEntry, TeamMemberTabScope } from "@asap/shared/schemas/team-member-select";
import type { ContactForSelect } from "@asap/shared/types/contact";
import type { ContractsTableQueryParams } from "@asap/shared/types/contract";
import type { CursorPagination } from "@asap/shared/types/pagination";
import type { Database } from "@asap/shared/types/supabase.gen";
import type { ContractContactType, ContractStatus, MissionStatus } from "@asap/shared/utils/supabase.enum";
import type { Contract } from "@asap/shared/utils/supabase.types";
import { translateContractStatus } from "@asap/shared/utils/translate";
import type { SortingState } from "@tanstack/vue-table";
import type { StatusBadgeVariant } from "~/components/shared/status-badge/types";
import { useApi } from "~/composables/useApi";
import { useContractListRefresh } from "~/components/contract/composables/useContractListRefresh";
import { useContractComment } from "~/components/contract-comment/composables/useContractComment";
import type { PostContractBody } from "@asap/shared/schemas/contract/mutation";
import type { PatchContractBody } from "@asap/shared/schemas/contract/patch-contract";
type ContractState = {
  status: ContractStatus;
};

export const contractState = reactive<ContractState>({
  status: "to_process",
});

export const CONTRACT_UPDATE_TRACKED_STATUSES: ContractStatus[] = [
  "to_process",
  "to_send",
  "to_modify_by_admin",
  "waiting_for_signature",
  "signed",
  "cancelled",
  "refused",
];

export const useContract = function () {
  const { addToastError } = useToast();
  const { t } = useI18n();
  const { isConsultant, isAccounting, isIndependentAdministrator, teamMember } = useUserMe();
  const { asapWorkspaceId } = useAsapWorkspace();
  const { invoke } = useApi();
  const { getLastCommentType } = useContractComment();
  const { trackEvent } = usePosthog();
  const { markContractListNeedsRefresh } = useContractListRefresh();

  const getContractTableRows = async (
    params: ContractsTableQueryParams,
    cursor?: CursorPagination,
    signal?: AbortSignal
  ) => {
    const response = await invoke({
      path: "contracts/table",
      method: "GET",
      query: { ...params, ...cursor },
      signal,
    });

    if (!response.success) {
      if (isAbortError(response.error)) return [];
      throw new Error(response.error.message);
    }

    return response.data;
  };

  const getExtensionAmendmentBatchPreview = async (params: ContractsTableQueryParams) => {
    const response = await invoke({
      path: "pld/contract-extension/batch-preview",
      method: "GET",
      query: params,
    });

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return response.data;
  };

  const sendExtensionAmendmentBatchToPld = async (params: ContractsTableQueryParams, expectedEligibleCount: number) => {
    const response = await invoke({
      path: "pld/contract-extension/batch-send",
      method: "POST",
      query: params,
      body: { expectedEligibleCount },
    });

    if (!response.success) {
      throw new Error(response.error.message, { cause: response.status });
    }

    return response.data;
  };

  const insert = async (
    contract: Database["public"]["Tables"]["contract"]["Insert"] &
      Pick<PostContractBody, "address_extra_info" | "close_mission"> & {
        remuneration_profile?: RemunerationProfileInsertPayload;
      }
  ) => {
    // Creation goes through the API so it emits ContractCreated (the notification pipeline keys off
    // the persisted status). The endpoint consumes multipart/form-data (a summary sheet may ride
    // along on other create paths); creation sends the data part only.
    const formData = new FormData();
    formData.append("data", JSON.stringify(contract));

    const createResponse = await invoke({ path: "contracts", method: "POST", body: formData });
    if (!createResponse.success) {
      throw new Error(createResponse.error.clientMessage || createResponse.error.message);
    }

    return { contractId: createResponse.data.id, contractKey: createResponse.data.contract_key };
  };

  const createWithAutoMission = async (body: PostContractWithAutoMissionBody) => {
    const response = await invoke({
      path: "contracts/with-auto-mission",
      method: "POST",
      body,
    });

    if (!response.success) {
      throw new Error(response.error.clientMessage || response.error.message);
    }

    trackEvent("mission_created", {
      mission_id: response.data.mission_id,
      contract_id: response.data.contract_id,
      company_id: body.mission_quick_start.company_id,
    });

    return {
      contractId: response.data.contract_id,
      contractKey: response.data.contract_key,
      missionId: response.data.mission_id,
    };
  };

  const createModificationAmendment = async (body: PostModificationAmendmentBody) => {
    const response = await invoke({
      path: "contracts/modification-amendments",
      method: "POST",
      body,
    });

    if (!response.success) {
      throw new Error(response.error.clientMessage || response.error.message);
    }

    return {
      contractId: response.data.id,
      contractKey: response.data.contract_key,
    };
  };

  const createExtensionAmendment = async (body: PostExtensionAmendmentBody) => {
    const response = await invoke({
      path: "contracts/extension-amendments",
      method: "POST",
      body,
    });

    if (!response.success) {
      throw new Error(response.error.clientMessage || response.error.message);
    }

    return {
      contractId: response.data.id,
      contractKey: response.data.contract_key,
    };
  };

  const selectSingle = async function (id: string) {
    const response = await invoke({
      path: "contracts/:contractId",
      method: "GET",
      params: { contractId: id },
    });

    if (!response.success) throw new Error(response.error.message);
    return { data: response.data };
  };

  const listTalentContractKeys = async function (talentId: string) {
    const response = await invoke({
      path: "contracts/by-talent/:talentId/contract-keys",
      method: "GET",
      params: { talentId },
    });

    if (!response.success) throw new Error(response.error.message);
    return response.data;
  };

  const getTalentContractSummary = async function (talentId: string) {
    const response = await invoke({
      path: "contracts/by-talent/:talentId/summary",
      method: "GET",
      params: { talentId },
    });

    if (!response.success) throw new Error(response.error.message);
    return response.data;
  };

  const selectContractKeyDetail = async function (contractKey: number) {
    const response = await invoke({
      path: "contracts/planning",
      method: "GET",
      query: { contract_key: contractKey },
    });

    if (!response.success) throw new Error(response.error.message);
    return { data: response.data };
  };

  const manageContractContact = async function (
    contractId: string,
    type: ContractContactType,
    contacts: ContactForSelect[],
    oldContacts: ContactForSelect[]
  ) {
    const { insertedItems, removedItems } = detectChangesInCollection(contacts, oldContacts);

    if (insertedItems.length > 0) {
      // The API ignores the contacts `ContractContactForm` may already have added
      const response = await invoke({
        path: "contracts/:contractId/contacts/:type",
        method: "POST",
        params: { contractId, type },
        body: { contactIds: insertedItems.map((c) => c.id) },
      });
      if (!response.success) {
        addToastError({ title: t("contracts.contacts.addErrorToast") }, response.error);
        throw new Error(response.error.message);
      }
    }

    for (const removedContact of removedItems) {
      const response = await invoke({
        path: "contracts/:contractId/contacts/:type/:contactId",
        method: "DELETE",
        params: { contractId, type, contactId: removedContact.id },
      });
      if (!response.success) {
        addToastError({ title: t("contracts.contacts.removeErrorToast") }, response.error);
        throw new Error(response.error.message);
      }
    }
  };

  const editContract = async (
    contract: Pick<Contract, "id" | "contract_key" | "status">,
    changes: PatchContractBody
  ) => {
    await patchContract(contract.id, changes);

    if (CONTRACT_UPDATE_TRACKED_STATUSES.includes(contract.status)) {
      trackEvent("contract_updated", {
        contract_id: contract.id,
        contract_key: contract.contract_key,
        status: contract.status,
        updated_fields: changes,
      });
    }
  };

  /** Sends a draft, or a contract sent back to its owner, to validation or straight to processing. */
  const submit = async (contract: Pick<Contract, "id" | "contract_key">) => {
    const response = await invoke({
      path: "contracts/:contractId/submission",
      method: "POST",
      params: { contractId: contract.id },
    });

    if (!response.success) throw new Error(response.error.message);

    markContractListNeedsRefresh();

    const { status } = response.data;
    if (CONTRACT_UPDATE_TRACKED_STATUSES.includes(status)) {
      trackEvent("contract_updated", {
        contract_id: contract.id,
        contract_key: contract.contract_key,
        status,
        updated_fields: { status },
      });
    }

    return status;
  };

  const validate = async (contract: Pick<Contract, "id" | "contract_key">) => {
    const response = await invoke({
      path: "contracts/:contractId/validation",
      method: "POST",
      params: { contractId: contract.id },
    });

    if (!response.success) throw new Error(response.error.message);

    markContractListNeedsRefresh();

    trackEvent("contract_updated", {
      contract_id: contract.id,
      contract_key: contract.contract_key,
      status: "to_process",
      updated_fields: { status: "to_process", validated_by: teamMember.value?.id },
    });
  };

  const markAsProcessed = async (contract: Pick<Contract, "id" | "contract_key">, processedStatus: ContractStatus) => {
    const response = await invoke({
      path: "contracts/:contractId/processing",
      method: "POST",
      params: { contractId: contract.id },
    });

    if (!response.success) throw new Error(response.error.message);

    markContractListNeedsRefresh();

    if (CONTRACT_UPDATE_TRACKED_STATUSES.includes(processedStatus)) {
      trackEvent("contract_updated", {
        contract_id: contract.id,
        contract_key: contract.contract_key,
        status: processedStatus,
        updated_fields: { status: processedStatus },
      });
    }
  };

  const validateAmendment = async (contract: Pick<Contract, "id" | "contract_key">) => {
    const response = await invoke({
      path: "contracts/:contractId/amendment-validation",
      method: "POST",
      params: { contractId: contract.id },
    });

    if (!response.success) throw new Error(response.error.message);

    markContractListNeedsRefresh();

    trackEvent("contract_updated", {
      contract_id: contract.id,
      contract_key: contract.contract_key,
      status: "to_send",
      updated_fields: { status: "to_send" },
    });
  };

  const markAsSent = async (contract: Pick<Contract, "id" | "contract_key">) => {
    const response = await invoke({
      path: "contracts/:contractId/sending",
      method: "POST",
      params: { contractId: contract.id },
    });

    if (!response.success) throw new Error(response.error.message);

    markContractListNeedsRefresh();

    trackEvent("contract_updated", {
      contract_id: contract.id,
      contract_key: contract.contract_key,
      status: "waiting_for_signature",
      updated_fields: { status: "waiting_for_signature" },
    });
  };

  const markAsSigned = async (contract: Pick<Contract, "id" | "contract_key">) => {
    const response = await invoke({
      path: "contracts/:contractId/signature",
      method: "POST",
      params: { contractId: contract.id },
    });

    if (!response.success) throw new Error(response.error.clientMessage || response.error.message);

    markContractListNeedsRefresh();

    trackEvent("contract_updated", {
      contract_id: contract.id,
      contract_key: contract.contract_key,
      status: "signed",
      updated_fields: { status: "signed" },
    });
  };

  const markDefinitiveEndAsProcessed = async (contractId: string) => {
    const response = await invoke({
      path: "contracts/:contractId/definitive-end/processing",
      method: "POST",
      params: { contractId },
    });

    if (!response.success) {
      addToastError({ title: t("contracts.definitiveEnd.processErrorToast") }, response.error);
      throw new Error(response.error.message);
    }
  };

  const changeOwner = async (contractId: string, ownerId: string) => {
    const response = await invoke({
      path: "contracts/:contractId/owner",
      method: "PUT",
      params: { contractId },
      body: { ownerId },
    });

    if (!response.success) {
      addToastError({ description: t("contracts.planningViewDetails.ownerChangeErrorToast") }, response.error);
      throw new Error(response.error.message);
    }
  };

  const cancelDefinitiveEnd = async (contractId: string) => {
    const response = await invoke({
      path: "contracts/:contractId/definitive-end",
      method: "DELETE",
      params: { contractId },
    });

    if (!response.success) {
      addToastError({ title: t("contracts.definitiveEnd.cancelErrorToast") }, response.error);
      throw new Error(response.error.message);
    }
  };

  interface StatusBadgeVariantConfig {
    consultant_team_member: StatusBadgeVariant;
    accounting_team_member: StatusBadgeVariant;
    manager_team_member: StatusBadgeVariant;
    default: StatusBadgeVariant;
  }

  const statusBadgeVariant: Record<ContractStatus, StatusBadgeVariantConfig> = {
    to_process: {
      consultant_team_member: "warning",
      accounting_team_member: "danger",
      manager_team_member: "warning",
      default: "warning",
    },
    to_send: {
      consultant_team_member: "danger",
      accounting_team_member: "warning",
      manager_team_member: "warning",
      default: "warning",
    },
    to_modify_by_owner: {
      consultant_team_member: "danger",
      accounting_team_member: "warning",
      manager_team_member: "warning",
      default: "warning",
    },
    to_modify_by_admin: {
      consultant_team_member: "warning",
      accounting_team_member: "danger",
      manager_team_member: "warning",
      default: "warning",
    },
    to_validate: {
      consultant_team_member: "warning",
      accounting_team_member: "warning",
      manager_team_member: "danger",
      default: "warning",
    },
    waiting_for_signature: {
      consultant_team_member: "warning",
      accounting_team_member: "warning",
      manager_team_member: "warning",
      default: "warning",
    },
    draft: {
      consultant_team_member: "info",
      accounting_team_member: "info",
      manager_team_member: "info",
      default: "info",
    },
    signed: {
      consultant_team_member: "success",
      accounting_team_member: "success",
      manager_team_member: "success",
      default: "success",
    },
    cancelled: {
      consultant_team_member: "neutral",
      accounting_team_member: "neutral",
      manager_team_member: "neutral",
      default: "neutral",
    },
    refused: {
      consultant_team_member: "danger",
      accounting_team_member: "danger",
      manager_team_member: "danger",
      default: "danger",
    },
  };

  /** `canValidate` comes from the API `actAsValidator` rule, true only on a contract to validate. */
  const getStatusBadgeData = (status: ContractStatus, canValidate = false) => {
    const role = getStatusBadgeRole({
      status,
      canValidate,
      isAccounting: isAccounting.value,
      isConsultant: isConsultant.value,
    });

    return {
      translation: translateContractStatus.fr[status]?.[role],
      variant: statusBadgeVariant[status][role],
    };
  };

  const isCurrentUserBadgedAsValidator = (status: ContractStatus) =>
    isBadgedAsValidatorOfOwnContract({
      status,
      isIndependentAdministrator: isIndependentAdministrator.value,
      teamMemberWorkspaceId: teamMember.value?.workspace_id,
      asapWorkspaceId: asapWorkspaceId.value,
    });

  const getContractPermissions = async (contractId: string) => {
    const response = await invoke({
      path: "permissions/contracts/:id",
      method: "GET",
      params: { id: contractId },
    });

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return response.data;
  };

  const contractCanBeEdited = async (contractId: string) => {
    const permissions = await getContractPermissions(contractId);
    return permissions.edit.isAuthorized;
  };

  const patchContract = async (id: string, changes: PatchContractBody) => {
    const response = await invoke({
      path: "contracts/:contractId",
      method: "PATCH",
      params: { contractId: id },
      body: changes,
    });

    if (!response.success) throw new Error(response.error.message);

    markContractListNeedsRefresh();
  };

  const requestCorrection = async (contractId: string, comment: string) => {
    const response = await invoke({
      path: "contracts/:contractId/correction-request",
      method: "POST",
      params: { contractId },
      body: { comment },
    });

    if (!response.success) {
      if (isAbortError(response.error)) return;
      throw new Error(response.error.message);
    }

    markContractListNeedsRefresh();
  };

  const requestChangesOnValidation = async (contractId: string, comment: string) => {
    const response = await invoke({
      path: "contracts/:contractId/validation-change-request",
      method: "POST",
      params: { contractId },
      body: { comment },
    });

    if (!response.success) {
      if (isAbortError(response.error)) return;
      throw new Error(response.error.message);
    }

    markContractListNeedsRefresh();
  };

  const requestPrecisionsOnProcessing = async (contractId: string, comment: string) => {
    const response = await invoke({
      path: "contracts/:contractId/processing-precision-request",
      method: "POST",
      params: { contractId },
      body: { comment },
    });

    if (!response.success) {
      if (isAbortError(response.error)) return;
      throw new Error(response.error.message);
    }

    markContractListNeedsRefresh();
  };

  const getContractReview = async (contract: Contract) => {
    const lastCommentType = contract.status === "to_modify_by_owner" ? await getLastCommentType(contract.id) : null;
    const reviewType = getContractReviewType({
      status: contract.status,
      isAmendment: contract.is_amendment === true,
      lastCommentType,
    });
    if (!reviewType) return { isReviewAccessible: false, reviewType };

    const permissions = await getContractPermissions(contract.id);
    return { isReviewAccessible: permissions.readReview.isAuthorized, reviewType };
  };

  const getContractsTransmissionType = async (contractKeys: number[]): Promise<ContractKeyTransmissionType[]> => {
    const response = await invoke({
      path: "contracts/transmission-type",
      method: "GET",
      query: { contractKeys },
    });

    if (!response.success) {
      throw new Error(response.error.message);
    }

    return response.data;
  };

  return {
    getContractsTransmissionType,
    getStatusBadgeData,
    isCurrentUserBadgedAsValidator,
    selectSingle,
    insert,
    createWithAutoMission,
    createModificationAmendment,
    createExtensionAmendment,
    editContract,
    submit,
    manageContractContact,
    selectContractKeyDetail,
    listTalentContractKeys,
    getTalentContractSummary,
    contractState,
    validate,
    markAsProcessed,
    validateAmendment,
    markAsSent,
    markAsSigned,
    markDefinitiveEndAsProcessed,
    cancelDefinitiveEnd,
    changeOwner,
    getContractTableRows,
    getExtensionAmendmentBatchPreview,
    sendExtensionAmendmentBatchToPld,
    contractCanBeEdited,
    getContractPermissions,
    requestCorrection,
    requestChangesOnValidation,
    requestPrecisionsOnProcessing,
    patchContract,
    getContractReview,
  };
};

type ContractsFilters = {
  pageIndex?: number;
  pageSize?: number;
  search?: string;
  companyId?: string;
  status?: ContractStatus;
  statuses?: ContractStatus[];
  planningStatus?: ("done" | "current" | "upcoming" | "late" | "almost_done")[];
  contractKey?: number;
  isAmendment?: boolean;
  businessUnitIds?: string[];
  teamMemberSelection?: TeamMemberSelectionEntry[];
  isTalentPresentOnApplication?: boolean;
  isSignatureOnWorksitePossible?: boolean;
  startDate?: string;
  endDate?: string;
  missionStatus?: MissionStatus;
  orders?: { column: string; direction: "asc" | "desc" }[];
};

export type ContractsState = {
  selectedFilters: ContractsFilters;
  totalItems: number;
  loading: boolean;
  sorting: SortingState;
};

const contractsState = reactive<ContractsState>({
  selectedFilters: {
    pageSize: 20,
    pageIndex: 0,
    isAmendment: undefined,
    status: undefined,
    statuses: undefined,
    planningStatus: undefined,
    companyId: undefined,
    search: undefined,
    contractKey: undefined,
    orders: [{ column: "end_date", direction: "asc" }],
    businessUnitIds: undefined,
    teamMemberSelection: undefined,
    isTalentPresentOnApplication: undefined,
    isSignatureOnWorksitePossible: undefined,
    startDate: undefined,
    endDate: undefined,
    missionStatus: undefined,
  },
  totalItems: 0,
  loading: false,
  sorting: [{ id: "end_date", desc: true }],
});

export const useContracts = function () {
  const { invoke } = useApi();

  const mapStatusToColor = (status: ContractStatus): Color => {
    switch (status) {
      case "to_process":
        return "yellow";
      case "to_send":
        return "warning";
      case "to_modify_by_owner":
        return "critical";
      case "to_modify_by_admin":
        return "warning";
      case "to_validate":
        return "warning";
      case "waiting_for_signature":
        return "warning";
      case "draft":
        return "info";
      case "signed":
        return "success";
      case "cancelled":
        return "gray";
      case "refused":
        return "critical";
      default:
        return "gray";
    }
  };

  const convertSortingToOrders = (sorting: SortingState): { column: string; direction: "asc" | "desc" }[] => {
    const idToColumn = (sortId: string): string => {
      switch (sortId) {
        case "mission_status":
          return "planning_status";
        default:
          return sortId;
      }
    };
    return sorting.map((sort) => ({
      column: idToColumn(sort.id),
      direction: sort.desc ? "desc" : "asc",
    }));
  };

  const selectMultipleForTable = async function (
    filters: ContractsFilters,
    signal?: AbortSignal,
    options?: { includeStats?: boolean; background?: boolean }
  ): Promise<{
    data: ContractListItem[];
    count: number;
    contractsQueryData?: ContractsQueryData;
  }> {
    // A background fetch (page prefetch) must not flash the table loading state
    if (!options?.background) contractsState.loading = true;

    try {
      const order = filters.orders?.at(0);

      const teamMemberIds = filters.teamMemberSelection
        ?.filter((idEntry: TeamMemberSelectionEntry): idEntry is { type: "id"; value: string } => idEntry.type === "id")
        .map((e) => e.value);
      const teamMemberGlobalScopes = filters.teamMemberSelection
        ?.filter(
          (scopeEntry: TeamMemberSelectionEntry): scopeEntry is { type: "filter"; value: TeamMemberTabScope } =>
            scopeEntry.type === "filter"
        )
        .map((e) => e.value);

      const response = await invoke({
        path: "contracts",
        method: "GET",
        query: {
          pageIndex: filters.pageIndex,
          pageSize: filters.pageSize,
          search: filters.search,
          orderColumn: order?.column,
          orderDirection: order?.direction,
          companyId: filters.companyId,
          status: filters.status,
          statuses: filters.statuses,
          isAmendment: filters.isAmendment,
          contractKey: filters.contractKey,
          businessUnitIds: filters.businessUnitIds,
          teamMemberIds,
          teamMemberGlobalScopes,
          startDate: filters.startDate,
          endDate: filters.endDate,
          planningStatus: filters.planningStatus,
          missionStatus: filters.missionStatus,
          isTalentPresentOnApplication: filters.isTalentPresentOnApplication,
          isSignatureOnWorksitePossible: filters.isSignatureOnWorksitePossible,
          includeStats: options?.includeStats,
        },
        signal,
      });

      if (!response.success) {
        throw new Error(response.error.message);
      }

      return {
        data: response.data.items,
        count: response.data.totalItems,
        contractsQueryData: response.data.data,
      };
    } finally {
      if (!signal?.aborted && !options?.background) {
        contractsState.loading = false;
      }
    }
  };

  return {
    contractsState,
    selectMultipleForTable,
    convertSortingToOrders,
    mapStatusToColor,
  };
};
